import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getAudit } from "../lib/api";
import type { AuditResult, AuditStatusResponse } from "../lib/types";
import ScoreGauge from "../components/ScoreGauge";
import ProgressSteps from "../components/ProgressSteps";
import CategoryCard from "../components/CategoryCard";
import QuickWins from "../components/QuickWins";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 60;

export default function Audit() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AuditStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: number | undefined;

    async function tick() {
      try {
        const res = await getAudit(id!);
        if (cancelled) return;
        setData(res);
        if (res.status === "complete" || res.status === "failed") return;
        pollCount.current += 1;
        if (pollCount.current > MAX_POLLS) {
          setError("Audit took too long. Try refreshing in a moment.");
          return;
        }
        timer = window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load audit");
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [id]);

  return (
    <div className="min-h-full bg-ink-50">
      <header className="border-b border-ink-100 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-ink-900 font-semibold tracking-tight">
            AEO Scanner
          </Link>
          <Link to="/" className="text-ink-500 hover:text-ink-900 text-sm">
            New audit
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {!data && !error && (
          <div className="text-ink-500 py-20 text-center">Loading...</div>
        )}

        {data && data.status !== "complete" && data.status !== "failed" && (
          <PendingView data={data} />
        )}

        {data && data.status === "failed" && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h2 className="text-red-700 font-semibold">Audit failed</h2>
            <p className="text-red-600 mt-2 text-sm">
              {data.error ?? "Unknown error during audit."}
            </p>
            <Link
              to="/"
              className="text-red-700 mt-4 inline-block underline text-sm"
            >
              Try a different URL
            </Link>
          </div>
        )}

        {data && data.status === "complete" && data.result && (
          <ResultView url={data.url} result={data.result} auditedAt={data.completedAt} />
        )}
      </main>
    </div>
  );
}

function PendingView({ data }: { data: AuditStatusResponse }) {
  return (
    <div className="grid gap-8 md:grid-cols-[1fr_320px]">
      <div className="rounded-xl border border-ink-200 bg-white p-8">
        <div className="text-ink-400 text-sm">Auditing</div>
        <div className="text-ink-900 mt-1 break-all font-medium">{data.url}</div>
        <div className="text-ink-500 mt-6 text-sm">
          We are crawling the page with a headless browser, parsing structured data,
          and running content and technical AEO checks. This usually takes 15 to 30 seconds.
        </div>
      </div>
      <aside className="rounded-xl border border-ink-200 bg-white p-6">
        <h2 className="text-ink-900 mb-4 font-medium">Progress</h2>
        <ProgressSteps status={data.status} step={data.step} />
      </aside>
    </div>
  );
}

interface ResultProps {
  url: string;
  result: AuditResult;
  auditedAt: string | null;
}

function ResultView({ url, result, auditedAt }: ResultProps) {
  const tech = buildTechnicalChecks(result);
  const content = buildContentChecks(result);

  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-ink-200 bg-white p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-ink-400 text-sm">Audit for</div>
            <div className="text-ink-900 mt-1 break-all text-lg font-medium">{url}</div>
            {auditedAt && (
              <div className="text-ink-400 mt-2 text-xs">
                Completed {new Date(auditedAt).toLocaleString()}
              </div>
            )}
          </div>
          <ScoreGauge score={result.overallScore} label="AEO score" />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-ink-900 text-xl font-semibold">Category breakdown</h2>
        <CategoryCard
          title="Technical AEO"
          score={result.technical.score}
          summary="Crawler access, structured data, metadata and rendering."
          checks={tech}
        />
        <CategoryCard
          title="Content structure"
          score={result.content.score}
          summary="FAQ, headings, passages and freshness signals."
          checks={content}
        />
        <CategoryCard
          title="AI citation visibility"
          score={0}
          summary="Coming in Phase 2: tests your brand against real AI engine queries."
          checks={[]}
          comingSoon
        />
        <CategoryCard
          title="Content quality"
          score={0}
          summary="Coming in Phase 2: LLM-based scoring of extractability and answer-first structure."
          checks={[]}
          comingSoon
        />
      </section>

      <section>
        <h2 className="text-ink-900 mb-4 text-xl font-semibold">Quick wins</h2>
        <QuickWins wins={result.quickWins} />
      </section>

      <section className="rounded-xl border border-ink-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-ink-900 font-medium">Share this audit</h3>
            <p className="text-ink-500 mt-1 text-sm">
              The URL below loads the same report whenever you visit it.
            </p>
          </div>
          <ShareLink />
        </div>
      </section>
    </div>
  );
}

function ShareLink() {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-900 hover:bg-ink-50"
    >
      {copied ? "Copied" : "Copy link"}
    </button>
  );
}

type CheckStatus = "pass" | "warn" | "fail" | "info";
interface Check {
  label: string;
  status: CheckStatus;
  detail?: string;
}

function buildTechnicalChecks(r: AuditResult): Check[] {
  const t = r.technical;
  const checks: Check[] = [];

  const robotsBlocked = t.robotsTxt.botsBlocked.length;
  checks.push({
    label: "Robots.txt accessibility for AI bots",
    status:
      robotsBlocked === 0
        ? "pass"
        : robotsBlocked < t.robotsTxt.botsAllowed.length
          ? "warn"
          : "fail",
    detail: t.robotsTxt.found
      ? `Allowed: ${t.robotsTxt.botsAllowed.join(", ") || "none"}${
          robotsBlocked > 0 ? `. Blocked: ${t.robotsTxt.botsBlocked.join(", ")}` : ""
        }`
      : "No robots.txt found; bots will assume open access.",
  });

  checks.push({
    label: "Schema markup",
    status:
      t.schemaMarkup.types.length >= 3
        ? "pass"
        : t.schemaMarkup.types.length >= 1
          ? "warn"
          : "fail",
    detail:
      t.schemaMarkup.types.length > 0
        ? `Detected: ${t.schemaMarkup.types.join(", ")}${
            t.schemaMarkup.missingRecommended.length > 0
              ? `. Missing recommended: ${t.schemaMarkup.missingRecommended.join(", ")}`
              : ""
          }`
        : "No JSON-LD or microdata detected.",
  });

  const m = t.metadata;
  checks.push({
    label: "Page metadata",
    status: m.issues.length === 0 ? "pass" : m.issues.length <= 2 ? "warn" : "fail",
    detail:
      m.issues.length === 0
        ? "Title, meta description, OG tags and canonical look healthy."
        : m.issues.join(". ") + ".",
  });

  checks.push({
    label: "Server-side rendering",
    status: t.rendering.serverRendered
      ? "pass"
      : t.rendering.jsContentRatio < 0.6
        ? "warn"
        : "fail",
    detail: t.rendering.serverRendered
      ? "Most content is present in the initial HTML response."
      : `About ${Math.round(t.rendering.jsContentRatio * 100)}% of content appears to require JavaScript rendering.`,
  });

  const sc = t.crawlability.statusCode;
  checks.push({
    label: "HTTP response",
    status: sc >= 200 && sc < 300 ? "pass" : sc >= 300 && sc < 400 ? "warn" : "fail",
    detail: `Status: ${sc}${
      t.crawlability.redirectChain.length > 0
        ? `. Redirects: ${t.crawlability.redirectChain.length}`
        : ""
    }${
      t.crawlability.brokenLinks.length > 0
        ? `. Broken links: ${t.crawlability.brokenLinks.length}`
        : ""
    }`,
  });

  return checks;
}

function buildContentChecks(r: AuditResult): Check[] {
  const c = r.content;
  const checks: Check[] = [];

  checks.push({
    label: "FAQ section",
    status: c.faqPresent && c.faqCount >= 5 ? "pass" : c.faqPresent ? "warn" : "fail",
    detail: c.faqPresent
      ? `Detected ${c.faqCount} question${c.faqCount === 1 ? "" : "s"} in FAQ-style content.`
      : "No FAQ section detected.",
  });

  checks.push({
    label: "Table of contents",
    status: c.tocPresent ? "pass" : "warn",
    detail: c.tocPresent
      ? "Found in-page navigation with anchor links."
      : "No table-of-contents pattern detected.",
  });

  const h = c.headingStructure;
  checks.push({
    label: "Heading hierarchy",
    status: h.h1Count === 1 && h.issues.length === 0 ? "pass" : h.issues.length > 0 ? "warn" : "fail",
    detail: `${h.h1Count} H1 tag${h.h1Count === 1 ? "" : "s"}, ${h.questionHeadings} question-style heading${
      h.questionHeadings === 1 ? "" : "s"
    }${h.issues.length > 0 ? `. Issues: ${h.issues.join("; ")}` : ""}`,
  });

  const p = c.passageAnalysis;
  checks.push({
    label: "Passage extractability",
    status:
      p.avgLength > 0 && p.avgLength <= 50
        ? "pass"
        : p.avgLength <= 150
          ? "warn"
          : "fail",
    detail:
      p.totalPassages > 0
        ? `Average passage length: ${p.avgLength} words across ${p.totalPassages} passages. ${p.longPassages} exceed 150 words.`
        : "Not enough body content to analyze passages.",
  });

  const f = c.freshness;
  const freshStatus: CheckStatus =
    f.status === "fresh" ? "pass" : f.status === "stale" ? "warn" : f.status === "critical" ? "fail" : "info";
  checks.push({
    label: "Content freshness",
    status: freshStatus,
    detail:
      f.lastUpdated
        ? `Last updated ${new Date(f.lastUpdated).toLocaleDateString()} (${f.contentAgeDays} days ago)${
            f.freshnessSchemaPresent ? " and dateModified is present in schema." : "."
          }`
        : "No 'last updated' date detected on the page.",
  });

  return checks;
}
